// eslint-disable-next-line import/no-relative-packages
import config_data from '../../../../../brand.config.json';
import { appendLangParam } from '../url/helpers';

export const getBrandDomain = (): string => {
    return (config_data as Record<string, unknown> & typeof config_data).brand_domain as string;
};

export const getBrandName = () => {
    return config_data.brand_name;
};

export const getBrandLogo = () => {
    return config_data.brand_logo;
};

/** Default trade category on first load (e.g. even_odd for digit circles). See brand.config.json platform.default_contract_type */
export const getDefaultContractType = (): string => {
    const platform = config_data.platform as { default_contract_type?: string };
    const value = platform?.default_contract_type?.trim();
    return value || 'even_odd';
};

/**
 * Runtime production check based on window.location.hostname.
 * Matches against `brand_hostname.production` AND any hostnames listed in the
 * optional `brand_hostname.production_aliases` array. The alias list lets us
 * deploy the same build to multiple production URLs (Netlify primary domain
 * + Cloudflare Pages mirror, embed-only iframe host, etc.) without each one
 * silently flipping to staging API endpoints. This is what was causing the
 * "Failed to load market data" error when the v2 dtrader was iframed from
 * Kicktrades into `dtraderv2.netlify.app`: the hostname didn't match the
 * single configured production value, so isProduction() returned false, the
 * REST client hit `staging-api.derivws.com`, and the parent's PRODUCTION
 * OAuth bearer was rejected with 401.
 */
export const isProduction = (): boolean => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;

    if (/localhost(:\d+)?$/i.test(hostname) || hostname === '127.0.0.1') {
        return false;
    }

    // Explicit staging host from brand.config
    if (/^staging\./i.test(hostname)) {
        return false;
    }

    const brand_hostname = config_data.brand_hostname as {
        staging: string;
        production: string;
        production_aliases?: string[];
    };

    if (hostname === brand_hostname.staging) {
        return false;
    }

    if (hostname === brand_hostname.production) return true;

    const aliases = brand_hostname.production_aliases;
    if (Array.isArray(aliases) && aliases.includes(hostname)) return true;

    // Preview deploys (Vercel / Netlify) must use production Deriv APIs when OAuth
    // clients are registered on auth.deriv.com — same rule as dbotspace parent.
    if (/\.vercel\.app$/i.test(hostname) || /\.netlify\.app$/i.test(hostname)) {
        return true;
    }

    // Custom domains (e.g. user-owned production host) default to production so we do
    // not silently hit staging-api.derivws.com and break market data / OAuth tokens.
    return true;
};

export const getBrandHostname = () => {
    const hostname = isProduction() ? config_data.brand_hostname.production : config_data.brand_hostname.staging;
    return substituteDerivDomain(hostname);
};

export const getBrandUrl = () => {
    const hostname = isProduction() ? config_data.brand_hostname.production : config_data.brand_hostname.staging;
    return `https://${substituteDerivDomain(hostname)}`;
};

export const getBrandHomeUrl = (language?: string) => {
    const baseUrl = `${getBrandUrl()}/home`;
    return appendLangParam(baseUrl, language);
};

export const getBrandLoginUrl = (language?: string) => {
    const baseUrl = `${getBrandUrl()}/login`;
    return appendLangParam(baseUrl, language);
};

export const getBrandSignupUrl = (language?: string) => {
    const baseUrl = `${getBrandUrl()}/signup`;
    return appendLangParam(baseUrl, language);
};

export const getPlatformName = () => {
    return config_data.platform.name;
};

export const getPlatformLogo = () => {
    return config_data.platform.logo;
};

// [AI]
export const getBrandLogoDark = (): string => {
    return (
        ((config_data as Record<string, unknown> & typeof config_data).brand_logo_dark as string) ??
        config_data.brand_logo
    );
};

export const getPlatformDescription = (): string => {
    return ((config_data.platform as Record<string, unknown>).description as string) ?? '';
};

export const getAppId = (): number => {
    // Allow callers (notably the parent Traderkit/Kicktrades shell when this app
    // is loaded inside an iframe) to override the brand-config app_id with a
    // value matching their OAuth grant. Without this, a bearer minted under
    // the parent's app_id would be rejected by Deriv with
    // "Token is not valid for current app ID." when this app calls
    // `Deriv-App-ID: <our brand id>` against the v4 REST API.
    if (typeof window !== 'undefined') {
        try {
            const override = window.localStorage.getItem('config.app_id');
            // Numeric legacy app_id only — OAuth client_ids are handled by getDerivAppIdHeader()
            if (override && /^\d+$/.test(override)) return Number(override);
        } catch {
            // localStorage unavailable (private mode, etc.) — fall through.
        }
    }

    const app_id = (config_data as Record<string, unknown> & typeof config_data).app_id as
        | { staging: number; production: number }
        | undefined;
    if (!app_id) return 16929;
    return isProduction() ? app_id.production : app_id.staging;
};

/**
 * Value for the `Deriv-App-ID` REST/OTP header.
 * Parent embeds pass their OAuth `client_id` (same ID bots use for commissions).
 * Falls back to numeric getAppId() when no override is set.
 */
export const getDerivAppIdHeader = (): string => {
    if (typeof window !== 'undefined') {
        try {
            const override = window.localStorage.getItem('config.app_id');
            if (override && /^[0-9A-Za-z_-]+$/.test(override.trim())) {
                return override.trim();
            }
        } catch {
            // fall through
        }
    }
    return String(getAppId());
};
// [/AI]

export const getDomainName = () => {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    if (!hostname) return '';
    // Split the hostname into parts
    const domainParts = hostname.split('.');

    // Ensure we have at least two parts (SLD and TLD)
    if (domainParts.length >= 2) {
        // Combine the SLD and TLD
        const domain = `${domainParts[domainParts.length - 2]}.${domainParts[domainParts.length - 1]}`;
        return domain;
    }

    return '';
};

/**
 * Replaces "deriv.com" in a URL with the current domain (e.g. deriv.be, deriv.me).
 * Returns the URL unchanged when running on localhost or an unrecognised hostname.
 */
const substituteDerivDomain = (url: string): string => {
    const domain = getDomainName();
    if (!domain || domain !== getBrandDomain()) return url;
    try {
        // Parse the URL so we only rewrite the hostname — not query params or path segments
        const parsed = new URL(url);
        parsed.hostname = parsed.hostname.replace(/deriv\.com$/, domain);
        return parsed.toString();
    } catch {
        // Fallback for non-absolute strings (e.g. "api-core.deriv.com", "home.deriv.com/dashboard")
        return url.replace(/deriv\.com/, domain);
    }
};

/**
 * Returns the current TLD (e.g. "deriv.be") only when it is a known brand domain.
 * Falls back to "deriv.com" on unrecognised hostnames to keep cookies on a trusted domain.
 */
export const getTrustedDomainName = (): string => {
    const domain = getDomainName();
    return domain === getBrandDomain() ? domain : 'deriv.com';
};

/**
 * Returns window.location.hostname for use as an OAuth redirect parameter,
 * but only if the current hostname belongs to a known brand domain or a
 * recognised Cloudflare Pages preview deployment.
 * Returns empty string on unrecognised hostnames to prevent open-redirect
 * attacks where an attacker-controlled copy of the app injects a redirect
 * back to their domain after authentication.
 */
const CLOUDFLARE_PAGES_PATTERN = /^[a-zA-Z0-9-]+\.derivatives-trader\.pages\.dev$/;
export const getRedirectHostname = (): string => {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    const domain = getDomainName();
    if (domain === getBrandDomain()) return hostname;
    if (CLOUDFLARE_PAGES_PATTERN.test(hostname)) return hostname;
    return '';
};

/**
 * Gets the v4 REST API base URL (e.g. "https://api.derivws.com")
 */
export const getApiV4BaseUrl = (): string => {
    const cfg = config_data as Record<string, unknown> & typeof config_data;
    const derivws = cfg.derivws as { staging: string; production: string } | undefined;
    if (!derivws) return 'https://api.derivws.com';
    return isProduction() ? derivws.production : derivws.staging;
};

/**
 * Guest / logged-out WebSocket URL. The v4 `options/ws/public` endpoint does not accept
 * anonymous handshakes — use the legacy v3 public socket with app_id (same as dbotspace).
 */
export const getLegacyPublicWebSocketURL = (appIdOverride?: number | string): string => {
    const appId = appIdOverride ?? getAppId();
    const lang =
        (typeof window !== 'undefined' &&
            (window.localStorage.getItem('i18n_language') ||
                window.localStorage.getItem('clients_country') ||
                'EN')) ||
        'EN';
    return `wss://ws.derivws.com/websockets/v3?app_id=${encodeURIComponent(String(appId))}&l=${encodeURIComponent(
        String(lang).toLowerCase()
    )}&brand=deriv`;
};

/**
 * Gets the auth base URL (e.g., "https://auth.deriv.com")
 */
export const getAuthBaseUrl = (): string => {
    return isProduction() ? config_data.auth.production : config_data.auth.staging;
};

export const getOAuthClientId = (): string => {
    const auth = config_data.auth as Record<string, unknown>;
    const from_config = typeof auth.oauth_client_id === 'string' ? auth.oauth_client_id.trim() : '';
    const client_id = (process.env.OAUTH_CLIENT_ID || from_config) as string;
    if (!client_id)
        throw new Error(
            'OAuth client ID is not set. Add `auth.oauth_client_id` in brand.config.json or set OAUTH_CLIENT_ID in the environment.'
        );
    return client_id;
};

/**
 * True when the current hostname may use `window.location.origin` as the OAuth2
 * redirect_uri (must match a URI registered for your Deriv OAuth client).
 * Mirrors the profitap pattern: same build can be opened on Netlify, Pages
 * mirrors, etc., without editing brand.config for each deploy hostname.
 */
export const isOAuthRedirectHostAllowed = (): boolean => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    const brand_hostname = config_data.brand_hostname as {
        staging: string;
        production: string;
        production_aliases?: string[];
    };
    if (isProduction()) {
        if (hostname === brand_hostname.production) return true;
        const aliases = brand_hostname.production_aliases;
        if (Array.isArray(aliases) && aliases.includes(hostname)) return true;
        if (/\.vercel\.app$/i.test(hostname) || /\.netlify\.app$/i.test(hostname)) return true;
        return true;
    }
    return hostname === brand_hostname.staging;
};

/**
 * Numeric Deriv `app_id` query param on the OAuth2 authorize URL (same pattern as
 * profitap: v2 `client_id` + `app_id`). Uses `auth.oauth_app_id` when set; otherwise
 * the same app id as `getAppId()` so tokens match `Deriv-App-ID` on REST calls.
 */
export const getOAuthAppId = (): string => {
    const auth = config_data.auth as Record<string, unknown>;
    const explicit = auth.oauth_app_id;
    if (typeof explicit === 'string' && explicit.trim() !== '') return explicit.trim();
    return String(getAppId());
};

export const getOAuthRedirectUri = (): string => {
    const auth = config_data.auth as Record<string, unknown> & { oauth_redirect_path?: string };
    const path_suffix = typeof auth.oauth_redirect_path === 'string' ? auth.oauth_redirect_path : '';

    const fallback = isProduction()
        ? ((auth.oauth_redirect_uri_production as string) ?? '')
        : ((auth.oauth_redirect_uri_staging as string) ?? '');

    if (typeof window !== 'undefined' && isOAuthRedirectHostAllowed()) {
        return `${window.location.origin}${path_suffix}`;
    }

    return fallback;
};

/**
 * Gets the WebSocket server URL with base path
 * @returns WebSocket server URL with base path (e.g., "staging-api-core.deriv.com/options/v1/ws")
 */
export const getWebSocketURL = (): string => {
    const base = isProduction() ? config_data.api_core.production : config_data.api_core.staging;
    return `${substituteDerivDomain(base)}/options/v1/ws`;
};

/**
 * Gets the whoami endpoint URL
 * @returns Whoami endpoint URL (e.g., "https://auth.deriv.com/sessions/whoami")
 */
export const getWhoAmIURL = (): string => {
    const base = isProduction() ? config_data.auth.production : config_data.auth.staging;
    return substituteDerivDomain(`${base}/sessions/whoami`);
};

/**
 * Gets the logout endpoint URL
 * @returns Logout endpoint URL (e.g., "https://auth.deriv.com/self-service/logout/browser")
 */
export const getLogoutURL = (): string => {
    const base = isProduction() ? config_data.auth.production : config_data.auth.staging;
    return substituteDerivDomain(`${base}/self-service/logout/browser`);
};

/**
 * Gets the API Core URL based on environment
 * @returns API Core base URL (without protocol)
 */
export const getApiCoreUrl = (): string => {
    const url = isProduction() ? config_data.api_core.production : config_data.api_core.staging;
    return substituteDerivDomain(url);
};

/**
 * Gets the full API Core URL with protocol
 * @returns Full API Core URL with https://
 */
export const getApiCoreBaseUrl = (): string => {
    return `https://${getApiCoreUrl()}`;
};

/**
 * Gets the API URL for account_list based on environment
 * @returns API base URL (without protocol), e.g. "api.deriv.be"
 */
export const getApiUrl = (): string => {
    const url = isProduction() ? config_data.api.production : config_data.api.staging;
    return substituteDerivDomain(url);
};

/**
 * Gets the full API URL for account_list with protocol
 * @returns Full API URL with https://, e.g. "https://api.deriv.be"
 */
export const getApiBaseUrl = (): string => {
    return `https://${getApiUrl()}`;
};

/**
 * Gets the Help Centre URL
 * @returns Help Centre URL (e.g., "https://trade.deriv.com/help-centre")
 */
export const getHomeUrl = (): string => {
    return substituteDerivDomain(((config_data.platform as Record<string, unknown>).home_url as string) ?? '');
};

export const getHelpCentreUrl = (): string => {
    return substituteDerivDomain(config_data.platform.help_centre_url);
};

export const getDepositUrl = (): string => {
    const deposit = (config_data as Record<string, unknown>).deposit_url as
        | { staging: string; production: string }
        | undefined;
    if (!deposit) return '';
    return substituteDerivDomain(isProduction() ? deposit.production : deposit.staging);
};

export const getSignupUrl = (): string => {
    const signup = (config_data as Record<string, unknown>).signup_url as
        | { staging: string; production: string }
        | undefined;
    if (!signup) return '';
    return substituteDerivDomain(isProduction() ? signup.production : signup.staging);
};

export const isFeatureEnabled = (feature: 'dark_mode' | 'language_switcher'): boolean => {
    const features = (config_data as Record<string, unknown>).features as Record<string, boolean> | undefined;
    return features?.[feature] ?? false;
};
